<script setup lang="ts">
import { useField, useForm } from 'vee-validate'
import { computed, ref } from 'vue'
import * as yup from 'yup'
import { getAllUsersAction } from '@/api/functions/transactions/get-transaction-details.action'
import { useQueryApi } from '@/composables/useApi'

interface OrderFormValues {
  requester: string
  service: string
  plan: string
  region: string
  orderType: string
  quantity: number | ''
  customFields: Record<string, string>
}

const isOpen = ref(false)
const currentStep = ref(1)

const initialValues: OrderFormValues = {
  requester: '',
  service: 'service-1',
  plan: 'plan-1',
  region: 'region-1',
  orderType: 'order-type-1',
  quantity: 1,
  customFields: {},
}

const formValues = ref<OrderFormValues>({ ...initialValues })

// Users
interface User {
  id: number
  uuid: string
  fullName: string
}

const { data: usersResponse } = useQueryApi({
  queryKey: ['users'],
  queryFn: () => getAllUsersAction(),
  staleTime: 1000 * 60 * 5,
})

const usersSelectOptions = computed(() => {
  const users = usersResponse.value?.data ?? []

  return users.map((user: User) => ({
    label: `\u2068${user.id}\u2069 | \u2068${user.fullName}\u2069`,
    value: user.uuid,
  }))
})

// Mock options
const serviceOptions = [
  {
    label: 'خرید اشتراک نتفلیکس',
    value: 'service-1',
  },
  {
    label: 'خرید اشتراک اسپاتیفای',
    value: 'service-2',
  },
  {
    label: 'خرید گیفت کارت',
    value: 'service-3',
  },
]

const planOptions = [
  {
    label: 'پلن یک ماهه',
    value: 'plan-1',
  },
  {
    label: 'پلن سه ماهه',
    value: 'plan-2',
  },
  {
    label: 'پلن یک ساله',
    value: 'plan-3',
  },
]

const regionOptions = [
  {
    label: 'آمریکا',
    value: 'region-1',
  },
  {
    label: 'انگلستان',
    value: 'region-2',
  },
  {
    label: 'آلمان',
    value: 'region-3',
  },
]

const orderTypeOptions = [
  {
    label: 'خرید',
    value: 'order-type-1',
  },
  {
    label: 'تمدید',
    value: 'order-type-2',
  },
  {
    label: 'ارتقا',
    value: 'order-type-3',
  },
]

// Mock price
const orderPrice = {
  serviceAmount: 100,
  currency: '$',
  exchangeRate: 230500,
  commission: 1152500,
  vat: 115250,
  finalAmount: 24317750,
}

// Validation
const schema = yup.object({
  requester: yup.string().required('انتخاب کاربر الزامی است'),

  service: yup.string().required('انتخاب سرویس الزامی است'),

  plan: yup.string().required('انتخاب پلن الزامی است'),

  region: yup.string().required('انتخاب منطقه الزامی است'),

  orderType: yup.string().required('نوع سفارش الزامی است'),

  quantity: yup
    .number()
    .typeError('تعداد باید عدد باشد')
    .required('تعداد الزامی است')
    .integer('تعداد باید عدد صحیح باشد')
    .positive('تعداد باید بیشتر از صفر باشد'),
})

// Form
const { handleSubmit, errors } = useForm<OrderFormValues>({
  validationSchema: schema,
  initialValues: {
    ...initialValues,
  },
})

// Fields
const { value: requester } = useField<string>('requester')
const { value: service } = useField<string>('service')
const { value: plan } = useField<string>('plan')
const { value: region } = useField<string>('region')
const { value: orderType } = useField<string>('orderType')
const { value: quantity } = useField<number | ''>('quantity')

// Submit
const onFormSubmit = handleSubmit((values) => {
  formValues.value = {
    ...values,
    customFields: {},
  }

  currentStep.value = 2
})

// Back
const onBack = () => {
  currentStep.value = 1
}

// Create order
const onCreateOrder = () => {
  console.log('Create order:', formValues.value)
}

// Close
const onClose = () => {
  isOpen.value = false
  currentStep.value = 1

  formValues.value = {
    ...initialValues,
  }

  requester.value = ''
  service.value = 'service-1'
  plan.value = 'plan-1'
  region.value = 'region-1'
  orderType.value = 'order-type-1'
  quantity.value = 1
}

// Open
// const onOpen = () => {
//     isOpen.value = true
// }
</script>

<template>
    <div>
      <!--  TODO: disabled in 2.1.4 version -->
        <!-- <Button @click="onOpen" :isAdmin="true" class="px-3! rounded-lg! h-8! py-0 text-[13px] font-medium">
            افزودن سفارش جدید
        </Button> -->

        <OffCanvasPanel v-model="isOpen" width="600px" mobile-height="100dvh">
            <template #header>
                <span class="font-medium text-base text-[#0F1114]">
                    {{ currentStep === 1 ? 'افزودن سفارش' : 'بررسی اطلاعات سفارش' }}
                </span>
            </template>

            <!-- Step 1 -->
            <form v-if="currentStep === 1" id="create-order-form" class="flex flex-col gap-6 px-5 pt-2 pb-8"
                @submit.prevent="onFormSubmit">
                <div class="flex flex-col lg:grid grid-cols-2 gap-5">
                    <!-- Requester -->
                    <div class="col-span-2">
                        <Select v-model="requester" name="requester" label="کاربر" :options="usersSelectOptions"
                            direction="ltr" :error="errors.requester" appearance="admin" :searchable="true" />
                    </div>

                    <!-- Service -->
                    <Select v-model="service" name="service" label="سرویس" :options="serviceOptions"
                        :error="errors.service" appearance="admin" :searchable="true" />

                    <!-- Plan -->
                    <Select v-model="plan" name="plan" label="پلن" :options="planOptions" :error="errors.plan"
                        appearance="admin" :searchable="true" />

                    <!-- Region -->
                    <Select v-model="region" name="region" label="منطقه" :options="regionOptions" :error="errors.region"
                        appearance="admin" :searchable="true" />

                    <!-- Order Type -->
                    <Select v-model="orderType" name="orderType" label="نوع سفارش" :options="orderTypeOptions"
                        :error="errors.orderType" appearance="admin" />

                    <!-- Quantity -->
                    <div class="col-span-2">
                        <InputWithCounter v-model="quantity" name="quantity" label="تعداد" type="number" suffix="عدد"
                            appearance="admin" :error="errors.quantity" />
                    </div>
                </div>
            </form>

            <!-- Step 2 -->
            <div v-else class="flex flex-col gap-5 px-5 pt-2 pb-8">
                <div class="rounded-xl border border-[#d8d8d8] p-5">
                    <h3 class="mb-5 font-medium text-[#1b325a]">
                        اطلاعات سفارش
                    </h3>

                    <div class="grid grid-cols-1 gap-4 text-sm">
                        <div class="flex items-center justify-between">
                            <span class="text-gray-500">
                                کاربر
                            </span>

                            <span>
                                {{ formValues.requester }}
                            </span>
                        </div>

                        <div class="flex items-center justify-between">
                            <span class="text-gray-500">
                                سرویس
                            </span>

                            <span>
                                {{ formValues.service }}
                            </span>
                        </div>

                        <div class="flex items-center justify-between">
                            <span class="text-gray-500">
                                پلن
                            </span>

                            <span>
                                {{ formValues.plan }}
                            </span>
                        </div>

                        <div class="flex items-center justify-between">
                            <span class="text-gray-500">
                                منطقه
                            </span>

                            <span>
                                {{ formValues.region }}
                            </span>
                        </div>

                        <div class="flex items-center justify-between">
                            <span class="text-gray-500">
                                نوع سفارش
                            </span>

                            <span>
                                {{ formValues.orderType }}
                            </span>
                        </div>

                        <div class="flex items-center justify-between">
                            <span class="text-gray-500">
                                تعداد
                            </span>

                            <span>
                                {{ formValues.quantity }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Price Summary -->
                <div class="flex flex-col gap-4 rounded-xl border border-[#d8d8d8] p-5">
                    <div class="flex items-center justify-between text-sm">
                        <span class="text-gray-500">
                            مبلغ سرویس
                        </span>

                        <span class="font-fa">
                            {{ orderPrice.currency }}{{ orderPrice.serviceAmount.toFixed(2) }}
                        </span>
                    </div>

                    <div class="flex items-center justify-between text-sm">
                        <span class="text-gray-500">
                            نرخ تبدیل
                        </span>

                        <span class="font-fa">
                            {{ orderPrice.exchangeRate.toLocaleString() }} تومان
                        </span>
                    </div>

                    <div class="flex items-center justify-between text-sm">
                        <span class="text-gray-500">
                            کارمزد
                        </span>

                        <span class="font-fa">
                            {{ orderPrice.commission.toLocaleString() }} تومان
                        </span>
                    </div>

                    <div class="flex items-center justify-between text-sm">
                        <span class="text-gray-500">
                            ارزش افزوده
                        </span>

                        <span class="font-fa">
                            {{ orderPrice.vat.toLocaleString() }} تومان
                        </span>
                    </div>

                    <div class="my-1 border-t border-[#e5e5e5]" />

                    <div class="flex items-center justify-between">
                        <span class="font-bold text-[#1b325a]">
                            مبلغ نهایی
                        </span>

                        <span class="font-fa text-lg font-bold text-[#1b325a]">
                            {{ orderPrice.finalAmount.toLocaleString() }} تومان
                        </span>
                    </div>
                </div>
            </div>

            <template #footer>
                <div class="flex justify-end gap-2">
                    <Button v-if="currentStep === 2" type="button" label="بازگشت" variant="outline" :isAdmin="true"
                        class="w-fit h-11 px-6!" size="sm" @click="onBack" />

                    <Button v-if="currentStep === 1" type="submit" form="create-order-form" label="ادامه"
                        :isAdmin="true" class="w-fit h-11 px-6!" size="sm" />

                    <Button v-else type="button" label="ثبت سفارش" :isAdmin="true" class="w-fit h-11 px-6!" size="sm"
                        @click="onCreateOrder" />
                </div>
            </template>
        </OffCanvasPanel>
    </div>
</template>

<style scoped></style>
